import React, {useEffect, useMemo} from "react"
import {Button, Descriptions, Tag} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {NavLink, useHistory, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {getAttributesVersion, setAttributesVersion, setWSAttributesVersion} from "../../store/attributesVersionReducer"
import {GroupVersionAttributeType} from "./JsonAttributes"
import {s3Url} from "../../api/API"
import {OptionType} from "../../types/types"

export const AttributesVersion = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const version = useSelector((state: StateType) => state.attributesVersionReducer.version)
    const ws = useSelector((state: StateType) => state.attributesVersionReducer.ws)
    const controller = useMemo(() => new AbortController(), [id])

    useEffect(() =>
    {
        if (id) dispatch(getAttributesVersion({id, controller}))

        return () =>
        {
            controller.abort()
            dispatch(setAttributesVersion(null))
        }
    }, [dispatch, id, controller])

    useEffect(() =>
    {
        if (ws && ws.id === id)
        {
            dispatch(getAttributesVersion({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSAttributesVersion(null))
    }, [dispatch, ws, id, controller])

    const attributesString = useMemo(() =>
        {
            if (!version?.attributes) return []

            const parsed: Array<GroupVersionAttributeType> = JSON.parse(version.attributes)

            if (parsed)
            {
                const dict: Record<number, {
                    groupId: number,
                    groupName: string,
                    groupIndex: number,
                    attrs: Array<{
                        index: number,
                        key: string,
                        value: string,
                        defaultValue: string | number
                        options: Array<OptionType>
                    }>
                }> = []

                dict[0] = {
                    groupId: 0,
                    groupName: "Без группы",
                    groupIndex: 0,
                    attrs: [],
                }

                parsed?.forEach(g =>
                {
                    dict[g.id] = {
                        groupId: g.id,
                        groupName: g.name,
                        groupIndex: g.index,
                        attrs: [],
                    }

                    g.attributes.forEach(a =>
                    {
                        const attr = {
                            index: a.index,
                            key: a.key,
                            value: a.value,
                            defaultValue: a.defaultValue,
                            options: a.options ?? [],
                        }

                        dict[g.id].attrs.push(attr)
                    })
                })

                return <div style={{display: "flex", flexDirection: "column", gap: 5}}>
                    {
                        Object
                            .values(dict)
                            .filter(i => i.attrs?.length > 0)
                            .sort((a, b) => a.groupIndex - b.groupIndex)
                            .map((i) => <div key={i.groupId.toString()}>
                                {i.groupName}:
                                <ul>
                                    {i.attrs.sort((a, b) => a.index - b.index).map(i => (
                                        <li key={i.key}>{i.value}</li>
                                    ))}
                                </ul>
                            </div>)
                    }
                </div>
            }
        },
        [version])

    if (!version) return <Loading/>

    return (
        <div className="description-page">
            <Helmet>
                <title>{version?.name}</title>
            </Helmet>
            <div className="description-header">
                <BackButton onClick={() => history.goBack()}/>
                <Button type="link" onClick={() => history.push(`/attributesVersionForm`, {version})}>Редактировать</Button>
            </div>

            <Descriptions bordered column={{xs: 1, sm: 1, md: 2}}>
                <Descriptions.Item label="Версия">{version.version}</Descriptions.Item>
                <Descriptions.Item label="Название">{version.name}</Descriptions.Item>
                <Descriptions.Item label="Создана">{moment(version.createdAt).format("DD.MM.YYYY HH:mm")}</Descriptions.Item>
                <Descriptions.Item label={version.active ? "Активирована" : "Обновлена"}>{moment(version.updatedAt).format("DD.MM.YYYY HH:mm")}</Descriptions.Item>
                <Descriptions.Item span={2} label="Файл прошивки">
                    <div className="description-item-status">
                        <Tag color={version.firmware ? "green" : "volcano"} style={{marginRight: 0}}>{version.firmware ? "Да" : "Нет"}</Tag>
                        {
                            version.firmware
                                ? <NavLink target="_blank"
                                           to={{pathname: `${s3Url}/${version.firmware}`}}
                                           style={{padding: "4px 15px", fontWeight: 600, fontSize: 14}}
                                >
                                    Скачать
                                </NavLink>
                                : null
                        }
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Атрибуты" style={{whiteSpace: "break-spaces"}}>{attributesString}</Descriptions.Item>
                <Descriptions.Item label="Комментарий" style={{whiteSpace: "break-spaces"}}>{version.comment}</Descriptions.Item>
                <Descriptions.Item label="Список изменений" style={{whiteSpace: "break-spaces"}}>{version.changelog}</Descriptions.Item>
            </Descriptions>
        </div>
    )
}